<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  bulkDeleteLilithFaqs,
  createLilithFaq,
  deleteLilithFaq,
  getLilithFaqDrilldown,
  getLilithFaqs,
  getLilithSuggestion,
  getLilithSuggestions,
  runLilithSuggestionAction,
  updateLilithFaq,
  updateLilithSuggestion,
} from 'dashboard/api/lilithKnowledge';

const props = defineProps({
  assistantId: {
    type: String,
    required: true,
  },
});

const { accountId } = useAccount();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const tabFromQuery = value =>
  value === 'suggestions' ? 'suggestions' : 'faqs';
const activeTab = ref(tabFromQuery(route.query.tab));

const setActiveTab = tab => {
  const nextTab = tabFromQuery(tab);
  activeTab.value = nextTab;

  const query = { ...route.query };
  if (nextTab === 'suggestions') {
    query.tab = 'suggestions';
  } else {
    delete query.tab;
  }

  const currentTab = tabFromQuery(route.query.tab);
  const hasRedundantFaqTab =
    nextTab === 'faqs' && route.query.tab !== undefined;
  if (currentTab !== nextTab || hasRedundantFaqTab) {
    router.replace({ query });
  }
};
const records = ref([]);
const meta = ref({ page: 1, total_count: 0, total_pages: 1 });
const capabilities = ref({
  can_manage_faqs: false,
  can_view_faq_usage: false,
  can_review_suggestions: false,
});
const suggestionCount = ref(0);
const loading = ref(false);
const error = ref('');
const search = ref('');
const selectedIds = ref(new Set());
let requestSequence = 0;

const editor = reactive({
  open: false,
  kind: 'faq',
  mode: 'create',
  id: null,
  title: '',
  content: '',
  revision: 1,
});
const sourceDetail = reactive({
  id: null,
  loading: false,
  error: '',
  observations: [],
});
const usageDetail = reactive({
  id: null,
  loading: false,
  error: '',
  records: [],
  meta: {
    current_page: 1,
    per_page: 25,
    total_count: 0,
    conversation_count: 0,
  },
});

const isFaqTab = computed(() => activeTab.value === 'faqs');
const canManageFaqs = computed(() => capabilities.value.can_manage_faqs);
const canViewFaqUsage = computed(() => capabilities.value.can_view_faq_usage);
const canReviewSuggestions = computed(
  () => capabilities.value.can_review_suggestions
);

const fetchRecords = async (page = 1) => {
  requestSequence += 1;
  const sequence = requestSequence;
  loading.value = true;
  error.value = '';
  selectedIds.value = new Set();
  try {
    const params = { page };
    const query = search.value.trim();
    if (query) params.search = query;
    const response = isFaqTab.value
      ? await getLilithFaqs(accountId.value, props.assistantId, params)
      : await getLilithSuggestions(accountId.value, props.assistantId, params);
    if (sequence !== requestSequence) return;
    records.value = response.data?.payload || [];
    meta.value = response.data?.meta || {
      page,
      total_count: 0,
      total_pages: 1,
    };
    suggestionCount.value = isFaqTab.value
      ? Number(meta.value.suggestion_count || 0)
      : Number(meta.value.total_count || 0);
    capabilities.value = response.data?.capabilities || capabilities.value;
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    error.value =
      requestError?.response?.data?.error ||
      requestError?.message ||
      t('TEAM_LILITH.KNOWLEDGE.ERROR');
  } finally {
    if (sequence === requestSequence) loading.value = false;
  }
};

const debouncedSearch = debounce(() => fetchRecords(1), 300);

const resetUsageDetail = () => {
  Object.assign(usageDetail, {
    id: null,
    loading: false,
    error: '',
    records: [],
    meta: {
      current_page: 1,
      per_page: 25,
      total_count: 0,
      conversation_count: 0,
    },
  });
};

const resetEditor = () => {
  Object.assign(editor, {
    open: false,
    kind: 'faq',
    mode: 'create',
    id: null,
    title: '',
    content: '',
    revision: 1,
  });
};

const openCreate = () => {
  resetEditor();
  editor.open = true;
};

const openEdit = item => {
  Object.assign(editor, {
    open: true,
    kind: item.kind || (isFaqTab.value ? 'faq' : 'suggestion'),
    mode: 'edit',
    id: item.id,
    title: item.title,
    content: item.content,
    revision: item.revision,
  });
};

const saveEditor = async () => {
  const title = editor.title.trim();
  const content = editor.content.trim();
  if (!title || !content) return;
  let needsReview = false;
  try {
    if (editor.mode === 'create') {
      await createLilithFaq(accountId.value, props.assistantId, {
        title,
        content,
      });
    } else if (editor.kind === 'suggestion') {
      await updateLilithSuggestion(
        accountId.value,
        props.assistantId,
        editor.id,
        {
          title,
          content,
          expected_revision: editor.revision,
        }
      );
    } else {
      const updated = await updateLilithFaq(accountId.value, props.assistantId, editor.id, {
        title,
        content,
        expected_revision: editor.revision,
      });
      needsReview = updated.data?.status === 'PENDING_REVIEW';
    }
    resetEditor();
    await fetchRecords(meta.value.page || 1);
    useAlert(t(needsReview ? 'TEAM_LILITH.KNOWLEDGE.REVIEW_REQUIRED' : 'TEAM_LILITH.KNOWLEDGE.SAVED'));
  } catch (requestError) {
    useAlert(
      requestError?.response?.data?.error ||
        requestError?.message ||
        t('TEAM_LILITH.KNOWLEDGE.ERROR')
    );
  }
};

const runSuggestionAction = async (item, action) => {
  try {
    await runLilithSuggestionAction(
      accountId.value,
      props.assistantId,
      item.id,
      action,
      item.revision
    );
    await fetchRecords(meta.value.page || 1);
  } catch (requestError) {
    useAlert(requestError?.response?.data?.error || requestError?.message);
  }
};

const toggleSelected = id => {
  const next = new Set(selectedIds.value);
  next[next.has(id) ? 'delete' : 'add'](id);
  selectedIds.value = next;
};

const deleteFaq = async item => {
  try {
    await deleteLilithFaq(accountId.value, props.assistantId, item.id);
    selectedIds.value.delete(item.id);
    await fetchRecords(meta.value.page || 1);
  } catch (requestError) {
    useAlert(
      requestError?.response?.data?.error ||
        requestError?.message ||
        t('TEAM_LILITH.KNOWLEDGE.ERROR')
    );
  }
};

const deleteSelected = async () => {
  const ids = records.value
    .filter(item => selectedIds.value.has(item.id))
    .map(item => item.id);
  if (!ids.length) return;
  try {
    await bulkDeleteLilithFaqs(accountId.value, props.assistantId, ids);
    selectedIds.value = new Set();
    await fetchRecords(meta.value.page || 1);
  } catch (requestError) {
    useAlert(
      requestError?.response?.data?.error ||
        requestError?.message ||
        t('TEAM_LILITH.KNOWLEDGE.ERROR')
    );
  }
};

const openSourceConversationRef = conversationRef => {
  if (!conversationRef) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversationRef,
    },
  });
};

const formatUsageDate = value => {
  if (!value) return '—';
  const numeric = Number(value);
  const normalized = Number.isFinite(numeric) ? numeric * 1000 : value;
  return new Date(normalized).toLocaleString();
};

const loadFaqUsage = async (item, page = 1) => {
  Object.assign(usageDetail, {
    id: item.id,
    loading: true,
    error: '',
    records: page === 1 ? [] : usageDetail.records,
  });
  try {
    const response = await getLilithFaqDrilldown(
      accountId.value,
      props.assistantId,
      item.id,
      { page }
    );
    if (usageDetail.id !== item.id) return;
    usageDetail.records = response.data?.payload || [];
    usageDetail.meta = response.data?.meta || {
      current_page: page,
      per_page: 25,
      total_count: 0,
      conversation_count: 0,
    };
  } catch (requestError) {
    if (usageDetail.id !== item.id) return;
    usageDetail.error =
      requestError?.response?.data?.error ||
      requestError?.message ||
      t('TEAM_LILITH.KNOWLEDGE.ERROR');
  } finally {
    if (usageDetail.id === item.id) usageDetail.loading = false;
  }
};

const toggleFaqUsage = async item => {
  if (usageDetail.id === item.id) {
    resetUsageDetail();
    return;
  }
  await loadFaqUsage(item, 1);
};

const openSourceConversation = item => {
  if (!item.source_conversation_accessible || !item.source_conversation_ref) {
    return;
  }
  openSourceConversationRef(item.source_conversation_ref);
};

const toggleSuggestionSources = async item => {
  if (sourceDetail.id === item.id) {
    Object.assign(sourceDetail, {
      id: null,
      loading: false,
      error: '',
      observations: [],
    });
    return;
  }
  Object.assign(sourceDetail, {
    id: item.id,
    loading: true,
    error: '',
    observations: [],
  });
  try {
    const response = await getLilithSuggestion(
      accountId.value,
      props.assistantId,
      item.id
    );
    if (sourceDetail.id !== item.id) return;
    sourceDetail.observations = response.data?.observations || [];
  } catch (requestError) {
    if (sourceDetail.id !== item.id) return;
    sourceDetail.error =
      requestError?.response?.data?.error ||
      requestError?.message ||
      t('TEAM_LILITH.KNOWLEDGE.ERROR');
  } finally {
    if (sourceDetail.id === item.id) sourceDetail.loading = false;
  }
};

const openSourceDocument = item => {
  if (!item.source_document_ref) return;
  router.push({
    name: 'lilith_documents',
    params: {
      accountId: route.params.accountId,
      assistantId: props.assistantId,
    },
    query: { source_id: item.source_document_ref },
  });
};

watch(activeTab, () => {
  search.value = '';
  resetEditor();
  resetUsageDetail();
  fetchRecords(1);
});
watch(
  () => route.query.tab,
  tab => {
    const nextTab = tabFromQuery(tab);
    if (activeTab.value !== nextTab) activeTab.value = nextTab;
  }
);
watch(
  () => props.assistantId,
  () => {
    search.value = '';
    activeTab.value = tabFromQuery(route.query.tab);
    selectedIds.value = new Set();
    resetEditor();
    resetUsageDetail();
    Object.assign(sourceDetail, {
      id: null,
      loading: false,
      error: '',
      observations: [],
    });
    fetchRecords(1);
  }
);

onMounted(() => fetchRecords(1));
</script>

<template>
  <div class="flex-1 overflow-y-auto bg-n-surface-1 px-6 py-8 lg:px-10">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.KNOWLEDGE.EYEBROW') }}
          </div>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.KNOWLEDGE.TITLE') }}
          </h1>
          <p class="mt-1 max-w-3xl text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.KNOWLEDGE.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="isFaqTab && canManageFaqs"
          :label="$t('TEAM_LILITH.KNOWLEDGE.ADD')"
          icon="i-lucide-plus"
          @click="openCreate"
        />
      </header>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex rounded-lg bg-n-alpha-2 p-1">
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-sm"
            :class="
              isFaqTab ? 'bg-n-solid-2 text-n-slate-12' : 'text-n-slate-11'
            "
            @click="setActiveTab('faqs')"
          >
            {{ $t('TEAM_LILITH.KNOWLEDGE.TAB_FAQS') }}
          </button>
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-sm"
            :class="
              !isFaqTab ? 'bg-n-solid-2 text-n-slate-12' : 'text-n-slate-11'
            "
            @click="setActiveTab('suggestions')"
          >
            {{ $t('TEAM_LILITH.KNOWLEDGE.TAB_SUGGESTIONS') }}
            <span v-if="suggestionCount" class="ms-1">
              {{ suggestionCount }}
            </span>
          </button>
        </div>
        <Input
          v-model="search"
          class="w-64"
          size="sm"
          type="search"
          :placeholder="$t('TEAM_LILITH.KNOWLEDGE.SEARCH')"
          @input="debouncedSearch"
        />
      </div>

      <section
        v-if="editor.open"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="grid gap-4">
          <Input
            v-model="editor.title"
            :label="$t('TEAM_LILITH.KNOWLEDGE.QUESTION')"
          />
          <TextArea
            v-model="editor.content"
            :label="$t('TEAM_LILITH.KNOWLEDGE.ANSWER')"
            auto-height
            resize
            min-height="8rem"
          />
          <div class="flex justify-end gap-2">
            <Button
              :label="$t('TEAM_LILITH.KNOWLEDGE.CANCEL')"
              variant="ghost"
              color="slate"
              @click="resetEditor"
            />
            <Button
              :label="$t('TEAM_LILITH.KNOWLEDGE.SAVE')"
              @click="saveEditor"
            />
          </div>
        </div>
      </section>

      <div
        v-if="isFaqTab && canManageFaqs && selectedIds.size"
        class="flex items-center justify-between rounded-lg border border-n-weak bg-n-alpha-1 px-4 py-3"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('TEAM_LILITH.KNOWLEDGE.SELECTED', { count: selectedIds.size })
          }}
        </span>
        <Button
          :label="$t('TEAM_LILITH.KNOWLEDGE.DELETE_SELECTED')"
          variant="faded"
          color="ruby"
          @click="deleteSelected"
        />
      </div>

      <div
        v-if="loading"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-8 text-center text-sm text-n-slate-10"
      >
        {{ $t('TEAM_LILITH.KNOWLEDGE.LOADING') }}
      </div>
      <div
        v-else-if="error"
        class="rounded-xl border border-n-ruby-6 bg-n-ruby-2 p-4 text-sm text-n-ruby-11"
      >
        {{ error }}
      </div>
      <div
        v-else-if="!records.length"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-8 text-center text-sm text-n-slate-10"
      >
        {{
          isFaqTab
            ? $t('TEAM_LILITH.KNOWLEDGE.EMPTY_FAQS')
            : $t('TEAM_LILITH.KNOWLEDGE.EMPTY_SUGGESTIONS')
        }}
      </div>
      <div v-else class="flex flex-col gap-3">
        <article
          v-for="item in records"
          :key="item.id"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
        >
          <div class="flex items-start gap-3">
            <input
              v-if="isFaqTab && canManageFaqs"
              type="checkbox"
              class="mt-1"
              :checked="selectedIds.has(item.id)"
              @change="toggleSelected(item.id)"
            />
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ item.title }}
                </h2>
              </div>
              <p
                class="mt-2 whitespace-pre-wrap text-sm leading-6 text-n-slate-11"
              >
                {{ item.content }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <Button
                  v-if="isFaqTab && canManageFaqs"
                  :label="$t('TEAM_LILITH.KNOWLEDGE.EDIT')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="openEdit(item)"
                />
                <Button
                  v-if="isFaqTab && canManageFaqs"
                  :label="$t('TEAM_LILITH.KNOWLEDGE.DELETE')"
                  size="sm"
                  variant="ghost"
                  color="ruby"
                  @click="deleteFaq(item)"
                />
                <Button
                  v-if="isFaqTab && canViewFaqUsage && item.origin === 'MANUAL'"
                  :label="
                    $t('TEAM_LILITH.KNOWLEDGE.USED_IN_CONVERSATIONS', {
                      count: Number(item.used_in_conversations_count || 0),
                    })
                  "
                  icon="i-lucide-messages-square"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="!Number(item.used_in_conversations_count || 0)"
                  @click="toggleFaqUsage(item)"
                />
                <template v-if="!isFaqTab && canReviewSuggestions">
                  <Button
                    :label="$t('TEAM_LILITH.KNOWLEDGE.REVIEW')"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    @click="openEdit(item)"
                  />
                  <Button
                    :label="$t('TEAM_LILITH.KNOWLEDGE.APPROVE')"
                    size="sm"
                    @click="runSuggestionAction(item, 'approve')"
                  />
                  <Button
                    :label="$t('TEAM_LILITH.KNOWLEDGE.DISMISS')"
                    size="sm"
                    variant="ghost"
                    color="ruby"
                    @click="runSuggestionAction(item, 'dismiss')"
                  />
                </template>
                <Button
                  v-if="!isFaqTab && item.source_count"
                  :label="
                    $t('TEAM_LILITH.KNOWLEDGE.SOURCES', {
                      count: item.source_count,
                    })
                  "
                  icon="i-lucide-messages-square"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="toggleSuggestionSources(item)"
                />
                <Button
                  v-if="!isFaqTab && item.source_conversation_accessible"
                  :label="$t('TEAM_LILITH.KNOWLEDGE.OPEN_SOURCE_CONVERSATION')"
                  icon="i-lucide-arrow-up-right"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="openSourceConversation(item)"
                />
                <Button
                  v-if="!isFaqTab && item.source_document_ref"
                  :label="$t('TEAM_LILITH.KNOWLEDGE.OPEN_SOURCE_DOCUMENT')"
                  icon="i-lucide-arrow-up-right"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="openSourceDocument(item)"
                />
              </div>
              <div
                v-if="isFaqTab && usageDetail.id === item.id"
                class="mt-4 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <div v-if="usageDetail.loading" class="text-xs text-n-slate-10">
                  {{ $t('TEAM_LILITH.KNOWLEDGE.USAGE_LOADING') }}
                </div>
                <div
                  v-else-if="usageDetail.error"
                  class="text-xs text-n-ruby-11"
                >
                  {{ usageDetail.error }}
                </div>
                <div
                  v-else-if="!usageDetail.records.length"
                  class="text-xs text-n-slate-10"
                >
                  {{ $t('TEAM_LILITH.KNOWLEDGE.USAGE_EMPTY') }}
                </div>
                <div v-else class="flex flex-col gap-3">
                  <div
                    v-for="record in usageDetail.records"
                    :key="`${item.id}:${record.conversation.id}`"
                    class="rounded-md bg-n-surface-1 p-3"
                  >
                    <div class="text-xs font-medium text-n-slate-12">
                      {{
                        $t('TEAM_LILITH.KNOWLEDGE.USAGE_CONVERSATION', {
                          id: record.conversation.display_id,
                        })
                      }}
                    </div>
                    <div class="mt-1 text-xs text-n-slate-10">
                      {{
                        $t('TEAM_LILITH.KNOWLEDGE.USAGE_LAST_ACTIVITY', {
                          date: formatUsageDate(
                            record.conversation.last_activity_at
                          ),
                        })
                      }}
                    </div>
                    <Button
                      class="mt-2"
                      :label="
                        $t('TEAM_LILITH.KNOWLEDGE.OPEN_USAGE_CONVERSATION')
                      "
                      icon="i-lucide-arrow-up-right"
                      size="sm"
                      variant="ghost"
                      color="slate"
                      @click="
                        openSourceConversationRef(
                          record.conversation.display_id
                        )
                      "
                    />
                  </div>
                  <div
                    v-if="
                      usageDetail.meta.total_count > usageDetail.meta.per_page
                    "
                    class="flex items-center justify-center gap-2"
                  >
                    <Button
                      :label="$t('TEAM_LILITH.KNOWLEDGE.PREVIOUS')"
                      size="sm"
                      variant="ghost"
                      color="slate"
                      :disabled="usageDetail.meta.current_page <= 1"
                      @click="
                        loadFaqUsage(item, usageDetail.meta.current_page - 1)
                      "
                    />
                    <Button
                      :label="$t('TEAM_LILITH.KNOWLEDGE.NEXT')"
                      size="sm"
                      variant="ghost"
                      color="slate"
                      :disabled="
                        usageDetail.meta.current_page *
                          usageDetail.meta.per_page >=
                        usageDetail.meta.total_count
                      "
                      @click="
                        loadFaqUsage(item, usageDetail.meta.current_page + 1)
                      "
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="!isFaqTab && sourceDetail.id === item.id"
                class="mt-4 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <div
                  v-if="sourceDetail.loading"
                  class="text-xs text-n-slate-10"
                >
                  {{ $t('TEAM_LILITH.KNOWLEDGE.LOADING_SOURCES') }}
                </div>
                <div
                  v-else-if="sourceDetail.error"
                  class="text-xs text-n-ruby-11"
                >
                  {{ sourceDetail.error }}
                </div>
                <div
                  v-else-if="!sourceDetail.observations.length"
                  class="text-xs text-n-slate-10"
                >
                  {{ $t('TEAM_LILITH.KNOWLEDGE.NO_ACCESSIBLE_SOURCES') }}
                </div>
                <div v-else class="flex flex-col gap-3">
                  <div
                    v-for="observation in sourceDetail.observations"
                    :key="`${item.id}:${observation.conversation_ref}`"
                    class="rounded-md bg-n-surface-1 p-3"
                  >
                    <div class="text-xs font-medium text-n-slate-12">
                      {{ observation.generated_question }}
                    </div>
                    <div class="mt-1 text-xs leading-5 text-n-slate-11">
                      {{ observation.generated_answer }}
                    </div>
                    <Button
                      v-if="observation.source_conversation_accessible"
                      class="mt-2"
                      :label="
                        $t('TEAM_LILITH.KNOWLEDGE.OPEN_SOURCE_CONVERSATION')
                      "
                      icon="i-lucide-arrow-up-right"
                      size="sm"
                      variant="ghost"
                      color="slate"
                      @click="
                        openSourceConversationRef(observation.conversation_ref)
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div
        v-if="meta.total_pages > 1"
        class="flex items-center justify-center gap-3"
      >
        <Button
          :label="$t('TEAM_LILITH.KNOWLEDGE.PREVIOUS')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="meta.page <= 1"
          @click="fetchRecords(meta.page - 1)"
        />
        <span class="text-xs text-n-slate-10">
          {{
            $t('TEAM_LILITH.KNOWLEDGE.PAGE_OF', {
              page: meta.page,
              total: meta.total_pages,
            })
          }}
        </span>
        <Button
          :label="$t('TEAM_LILITH.KNOWLEDGE.NEXT')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="meta.page >= meta.total_pages"
          @click="fetchRecords(meta.page + 1)"
        />
      </div>
    </div>
  </div>
</template>
